import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';
import type { 
  Goal, 
  GoalTask, 
  GoalFormData, 
  GoalFilters, 
  GoalSortOptions, 
  GoalsResponse, 
  GoalResponse, 
  GoalsStats 
} from '@/types/goals';

// =====================================================
// GOAL CRUD OPERATIONS
// =====================================================

/**
 * Create a new goal
 */
export async function createGoal(goalData: any, userId: string): Promise<GoalResponse> {
  try {
    const tenantId = await getTenantId();
    
    // Map form data from Portuguese field names to English database field names
    const mappedData = {
      user_id: userId,
      tenant_id: tenantId,
      title: goalData.titulo || goalData.title || '',
      life_area: goalData.areaVida || goalData.life_area || null,
      current_state: goalData.estadoAtual || goalData.current_state || null,
      future_goal: goalData.metaFutura || goalData.future_goal || null,
      target_date: goalData.dataAlvo || goalData.target_date || null,
      start_date: goalData.dataInicio || goalData.start_date || null,
      budget: goalData.orcamento || goalData.budget || null,
      responsible: goalData.responsavel || goalData.responsible || null,
      who_can_help: goalData.quemPodeAjudar || goalData.who_can_help || null,
      how_will_be_done: goalData.comoSeraFeito || goalData.how_will_be_done || null,
      measures: goalData.medidas || goalData.measures || null,
      observations: goalData.observacoes || goalData.observations || null,
      status: 'planned'
    };
    
    console.log('🔍 Creating goal with mapped data:', mappedData);
    
    const { data, error } = await supabase
      .from('goals')
      .insert([mappedData])
      .select()
      .single();

    if (error) {
      console.error('Error creating goal:', error);
      return { data: null, error: error.message };
    }

    // Create related tasks if any
    const relatedTasks = goalData.tarefasRelacionadas || goalData.related_tasks || [];
    if (relatedTasks.length > 0) {
      await createGoalTasks(data.id, userId, relatedTasks);
    }

    return { data, error: undefined };
  } catch (error) {
    console.error('Error in createGoal:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get goals for a user with optional filters and sorting
 */
export async function getGoals(
  userId: string,
  filters?: GoalFilters,
  sortOptions?: GoalSortOptions,
  limit: number = 100,
  offset: number = 0
): Promise<GoalsResponse> {
  try {
    const tenantId = await getTenantId();
    
    let query = supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', tenantId);

    // Apply filters
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    
    if (filters?.life_area) {
      query = query.eq('life_area', filters.life_area);
    }
    
    if (filters?.date_range) {
      if (filters.date_range.start) {
        query = query.gte('target_date', filters.date_range.start);
      }
      if (filters.date_range.end) {
        query = query.lte('target_date', filters.date_range.end);
      }
    }

    // Apply sorting
    if (sortOptions) {
      query = query.order(sortOptions.field, { ascending: sortOptions.direction === 'ASC' });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching goals:', error);
      return { data: [], count: 0, error: error.message };
    }

    return { data: data || [], count: count || 0, error: undefined };
  } catch (error) {
    console.error('Error in getGoals:', error);
    return { data: [], count: 0, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get a single goal by ID
 */
export async function getGoalById(goalId: string, userId: string): Promise<GoalResponse> {
  try {
    const tenantId = await getTenantId();
    
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('id', goalId)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      console.error('Error fetching goal:', error);
      return { data: null, error: error.message };
    }

    return { data, error: undefined };
  } catch (error) {
    console.error('Error in getGoalById:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Update a goal
 */
export async function updateGoal(goalId: string, updates: any, userId: string): Promise<GoalResponse> {
  try {
    const tenantId = await getTenantId();
    
    const updateData: any = {};
    
    // Map form data from Portuguese field names to English database field names
    if (updates.titulo !== undefined || updates.title !== undefined) {
      updateData.title = updates.titulo || updates.title || '';
    }
    if (updates.areaVida !== undefined || updates.life_area !== undefined) {
      updateData.life_area = updates.areaVida || updates.life_area || null;
    }
    if (updates.estadoAtual !== undefined || updates.current_state !== undefined) {
      updateData.current_state = updates.estadoAtual || updates.current_state || null;
    }
    if (updates.metaFutura !== undefined || updates.future_goal !== undefined) {
      updateData.future_goal = updates.metaFutura || updates.future_goal || null;
    }
    if (updates.dataAlvo !== undefined || updates.target_date !== undefined) {
      updateData.target_date = updates.dataAlvo || updates.target_date || null;
    }
    if (updates.dataInicio !== undefined || updates.start_date !== undefined) {
      updateData.start_date = updates.dataInicio || updates.start_date || null;
    }
    if (updates.orcamento !== undefined || updates.budget !== undefined) {
      updateData.budget = updates.orcamento || updates.budget || null;
    }
    if (updates.responsavel !== undefined || updates.responsible !== undefined) {
      updateData.responsible = updates.responsavel || updates.responsible || null;
    }
    if (updates.quemPodeAjudar !== undefined || updates.who_can_help !== undefined) {
      updateData.who_can_help = updates.quemPodeAjudar || updates.who_can_help || null;
    }
    if (updates.comoSeraFeito !== undefined || updates.how_will_be_done !== undefined) {
      updateData.how_will_be_done = updates.comoSeraFeito || updates.how_will_be_done || null;
    }
    if (updates.medidas !== undefined || updates.measures !== undefined) {
      updateData.measures = updates.medidas || updates.measures || null;
    }
    if (updates.observacoes !== undefined || updates.observations !== undefined) {
      updateData.observations = updates.observacoes || updates.observations || null;
    }

    const { data, error } = await supabase
      .from('goals')
      .update(updateData)
      .eq('id', goalId)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId)
      .select()
      .single();

    if (error) {
      console.error('Error updating goal:', error);
      return { data: null, error: error.message };
    }

    // Update related tasks if provided
    const relatedTasks = updates.tarefasRelacionadas || updates.related_tasks;
    if (relatedTasks !== undefined) {
      await updateGoalTasks(goalId, userId, relatedTasks);
    }

    return { data, error: undefined };
  } catch (error) {
    console.error('Error in updateGoal:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Delete a goal
 */
export async function deleteGoal(goalId: string, userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const tenantId = await getTenantId();
    
    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', goalId)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('Error deleting goal:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in deleteGoal:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Update goal status
 */
export async function updateGoalStatus(
  goalId: string, 
  newStatus: string, 
  userId: string, 
  reason?: string
): Promise<GoalResponse> {
  try {
    const tenantId = await getTenantId();
    
    const { data, error } = await supabase
      .from('goals')
      .update({ status: newStatus })
      .eq('id', goalId)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId)
      .select()
      .single();

    if (error) {
      console.error('Error updating goal status:', error);
      return { data: null, error: error.message };
    }

    return { data, error: undefined };
  } catch (error) {
    console.error('Error in updateGoalStatus:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get goals statistics for a user
 */
export async function getGoalsStats(userId: string): Promise<{ data: GoalsStats | null; error?: string }> {
  try {
    const tenantId = await getTenantId();
    
    const { data, error } = await supabase
      .rpc('get_user_goals_stats', {
        user_uuid: userId,
        tenant_slug: tenantId
      });

    if (error) {
      console.error('Error fetching goals stats:', error);
      return { data: null, error: error.message };
    }

    return { data: data?.[0] || null, error: undefined };
  } catch (error) {
    console.error('Error in getGoalsStats:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// =====================================================
// GOAL TASKS OPERATIONS
// =====================================================

/**
 * Create tasks for a goal
 */
async function createGoalTasks(goalId: string, userId: string, taskTitles: string[]): Promise<void> {
  try {
    const tenantId = await getTenantId();
    
    const tasks = taskTitles.map(title => ({
      goal_id: goalId,
      user_id: userId,
      tenant_id: tenantId,
      title: title,
      status: 'pending'
    }));

    const { error } = await supabase
      .from('goal_tasks')
      .insert(tasks);

    if (error) {
      console.error('Error creating goal tasks:', error);
    }
  } catch (error) {
    console.error('Error in createGoalTasks:', error);
  }
}

/**
 * Update tasks for a goal
 */
async function updateGoalTasks(goalId: string, userId: string, taskTitles: string[]): Promise<void> {
  try {
    const tenantId = await getTenantId();
    
    // Delete existing tasks
    await supabase
      .from('goal_tasks')
      .delete()
      .eq('goal_id', goalId)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId);

    // Create new tasks
    if (taskTitles.length > 0) {
      await createGoalTasks(goalId, userId, taskTitles);
    }
  } catch (error) {
    console.error('Error in updateGoalTasks:', error);
  }
}

/**
 * Get tasks for a goal
 */
export async function getGoalTasks(goalId: string, userId: string): Promise<{ data: GoalTask[]; error?: string }> {
  try {
    const tenantId = await getTenantId();
    
    const { data, error } = await supabase
      .from('goal_tasks')
      .select('*')
      .eq('goal_id', goalId)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching goal tasks:', error);
      return { data: [], error: error.message };
    }

    return { data: data || [], error: undefined };
  } catch (error) {
    console.error('Error in getGoalTasks:', error);
    return { data: [], error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// =====================================================
// MIGRATION OPERATIONS
// =====================================================

/**
 * Migrate localStorage data to Supabase
 */
export async function migrateLocalStorageGoals(
  userId: string, 
  localStorageData: any[]
): Promise<{ success: boolean; migratedCount: number; error?: string }> {
  try {
    const tenantId = await getTenantId();
    
    const { data, error } = await supabase
      .rpc('migrate_localstorage_goals', {
        user_uuid: userId,
        tenant_slug: tenantId,
        goals_data: localStorageData
      });

    if (error) {
      console.error('Error migrating localStorage goals:', error);
      return { success: false, migratedCount: 0, error: error.message };
    }

    const migratedCount = data?.[0]?.migrated_count || 0;
    return { success: true, migratedCount, error: undefined };
  } catch (error) {
    console.error('Error in migrateLocalStorageGoals:', error);
    return { 
      success: false, 
      migratedCount: 0, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Check if user has localStorage data to migrate
 */
export function hasLocalStorageData(): boolean {
  try {
    const data = localStorage.getItem('goals_map_data');
    return data !== null && data !== '[]' && data !== 'null';
  } catch (error) {
    console.error('Error checking localStorage data:', error);
    return false;
  }
}

/**
 * Get localStorage data
 */
export function getLocalStorageData(): any[] {
  try {
    const data = localStorage.getItem('goals_map_data');
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error getting localStorage data:', error);
    return [];
  }
}

/**
 * Clear localStorage data after migration
 */
export function clearLocalStorageData(): void {
  try {
    localStorage.removeItem('goals_map_data');
  } catch (error) {
    console.error('Error clearing localStorage data:', error);
  }
}
